/*
Origin Core 1.0

called from /snippets/predictive-search.liquid

DEVELOPER NOTE
--------------------
any changes to this file should be noted here, and also noted in the following wiki page:
https://unifiedco.atlassian.net/wiki/spaces/ORIGIN/pages/1413414932/Core+File+Changes+1.0

SUMMARY
--------------------
This script defines two custom web components for Shopify product forms:

<add-to-cart-component> manages the add to cart button, including enabling/disabling, handling clicks, animating the button, and triggering a "fly to cart" animation when an item is added.
<product-form-component> manages the product form, handling variant selection, form submission, AJAX cart updates, error handling, and updating UI elements and accessibility live regions.
The script also defines a <fly-to-cart> component that animates a product image flying from the add to cart button to the cart icon using a cubic Bézier curve.
All components are registered if not already defined.

*/

import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd, preloadImage } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, VariantUpdateEvent } from '@theme/events';
import { checkoutAvailability, AVAILABILITY_PROPERTY } from './checkout-availability-data.js';
import { morph } from '@theme/morph'


export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number | undefined} */
  #animationTimeout;

  /** @type {number | undefined} */
  #cleanupTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Sets the loading state of the add to cart button.
   * @param {boolean} isLoading
   */
  setLoading(isLoading) {
    const { addToCartButton } = this.refs;
    addToCartButton.classList.toggle('atc-loading', isLoading);
    addToCartButton.disabled = isLoading;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    this.animateAddToCart();

    if (!event.target.closest('.quick-add-modal')) this.#animateFlyToCart();
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart() {
    const { addToCartButton } = this.refs;

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    if (!addToCartButton.classList.contains('atc-added')) {
      addToCartButton.classList.add('atc-added');
    }

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        this.refs.addToCartButton.classList.remove('atc-added');
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();
  #fetchAbortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.#fetchAbortController.abort();
  }

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  handleSubmit(event) {
    const { addToCartTextError } = this.refs;
    const submitter = event.submitter instanceof HTMLButtonElement ? event.submitter : null;
    const activeButton =
      submitter ?? this.refs.addToCartButtonContainer?.refs.addToCartButton;
    const activeButtonContainer =
      submitter?.closest('add-to-cart-component') ?? this.refs.addToCartButtonContainer;

    // Stop default behaviour from the browser
    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);

    // Check if the add to cart button is disabled and do an early return if it is
    if (activeButton?.disabled || activeButton?.getAttribute('disabled') === 'true') return;

    // Send the add to cart information to the cart
    const form = this.querySelector('form');

    if (!form) throw new Error('Product form element missing');

    const formData = new FormData(form);
    const personalisationSubmitModeInput = /** @type {HTMLInputElement | null} */ (
      form.querySelector('[data-personalisation-submit-mode]')
    );
    const isPersonalisationSubmit =
      submitter?.hasAttribute('data-personalisation-submit') || personalisationSubmitModeInput?.value === 'true';

    if (personalisationSubmitModeInput) {
      personalisationSubmitModeInput.value = 'false';
    }

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    });
    const sections = cartItemComponentsSectionIds.join(',');

    if (!formData.has('sections')) {
      formData.append('sections', sections);
    }

    let fetchCfg;
    let requestBody = formData;
    let requestPromise;
    let itemCount = Number(formData.get('quantity')) || 1;
    let eventVariantId = formData.get('id');
    const personalisationComponent = this.querySelector('personalisation-component');
    const shouldSubmitAsBundle = isPersonalisationSubmit;

    this.#fetchAbortController.abort();
    this.#fetchAbortController = new AbortController();

    if (shouldSubmitAsBundle) {
      const selectedCharmButton = /** @type {HTMLButtonElement | null} */ (
        personalisationComponent?.querySelector('[data-option-type="charm"][data-selected="true"]')
      );
      const selectedGemstoneButton = /** @type {HTMLButtonElement | null} */ (
        personalisationComponent?.querySelector('[data-option-type="gemstone"][data-selected="true"]')
      );
      const personalisationMessageInput = /** @type {HTMLInputElement | null} */ (
        personalisationComponent?.querySelector('[ref="messageInput"]')
      );
      const charmVariantId =
        /** @type {HTMLInputElement | null} */ (form.querySelector('[data-personalisation-selected-charm-id]'))?.value ||
        selectedCharmButton?.dataset.variantId;
      const gemstoneVariantId =
        /** @type {HTMLInputElement | null} */ (form.querySelector('[data-personalisation-selected-gemstone-id]'))?.value ||
        selectedGemstoneButton?.dataset.variantId;
      const personalisationMessage =
        /** @type {HTMLInputElement | null} */ (form.querySelector('[data-personalisation-message]'))?.value?.trim() ||
        personalisationMessageInput?.value?.trim();
      const quantity = Number(formData.get('quantity')) || 1;
      const baseVariantId = formData.get('id');
      const groupId = `personalisation-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const childItems = [];

      if (!baseVariantId) throw new Error('Form ID is required');

      const parentItem = {
        id: Number(baseVariantId),
        quantity,
        properties: {
          _personalisation_group: groupId,
        },
      };

      if (charmVariantId) {
        const charmProperties = {
          _personalisation_group: groupId,
          _personalisation_role: 'charm',
        };

        if (personalisationMessage) {
          charmProperties['Stamped message'] = personalisationMessage;
        }

        childItems.push({
          id: Number(charmVariantId),
          quantity,
          properties: charmProperties,
        });
      }

      if (gemstoneVariantId) {
        childItems.push({
          id: Number(gemstoneVariantId),
          quantity,
          properties: {
            _personalisation_group: groupId,
            _personalisation_role: 'gemstone',
          },
        });
      }

      itemCount = quantity;
      eventVariantId = baseVariantId;
      requestPromise = checkoutAvailability(this.dataset.productUrl, baseVariantId).then((availability) => {
        if (availability) parentItem.properties[AVAILABILITY_PROPERTY] = availability;
        return this.#submitBundleAdd({
          parentItem,
          childItems,
          sections,
          sectionsUrl: window.location.pathname + window.location.search,
        });
      });
    } else {
      fetchCfg = fetchConfig('javascript', { body: requestBody });
      requestPromise = checkoutAvailability(this.dataset.productUrl, eventVariantId).then((availability) => {
        if (availability) formData.set(`properties[${AVAILABILITY_PROPERTY}]`, availability);
        return fetch(Theme.routes.root + 'cart/add.js', {
          ...fetchCfg,
          signal: this.#fetchAbortController.signal,
          headers: {
            ...fetchCfg.headers,
            Accept: 'text/html',
          },
        }).then((response) => response.json());
      });
    }

    activeButtonContainer?.setLoading?.(true);

    requestPromise
      .then((response) => {
        if (response.status) {
          window.dispatchEvent(new CartErrorEvent(this.id, response.message));

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, 10000);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount,
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = eventVariantId;

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          if (activeButton) {
            const addToCartButton = activeButton;
            const addedTextElement = addToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, 5000);
          }

          const cartAddEvent = new CartAddEvent({}, id.toString(), {
            source: 'product-form-component',
            itemCount,
            productId: this.dataset.productId,
            sections: response.sections,
          });

          this.dispatchEvent(cartAddEvent);
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        activeButtonContainer?.setLoading?.(false);
      });
  }

  async #submitBundleAdd({ parentItem, childItems, sections, sectionsUrl }) {
    const parentResponse = await this.#postCartAdd({
      items: [parentItem],
      sections,
      sections_url: sectionsUrl,
    });

    if (parentResponse.status || !childItems.length) {
      return parentResponse;
    }

    const parentLineKey = parentResponse.items?.[0]?.key || '';

    if (!parentLineKey) {
      throw new Error('Unable to determine the parent cart line for personalisation');
    }

    const nestedChildren = childItems.map((item) => ({
      ...item,
      parent_line_key: parentLineKey,
    }));

    const childResponse = await this.#postCartAdd({
      items: nestedChildren,
      sections,
      sections_url: sectionsUrl,
    });

    if (childResponse.status) {
      await this.#rollbackParentLine(parentLineKey, sections);
      return childResponse;
    }

    return childResponse;
  }

  #postCartAdd(payload) {
    const body = JSON.stringify(payload);
    const fetchCfg = fetchConfig('json', { body });

    return fetch(Theme.routes.root + 'cart/add.js', {
      ...fetchCfg,
      signal: this.#fetchAbortController.signal,
      headers: {
        ...fetchCfg.headers,
        Accept: 'application/json',
      },
    }).then((response) => response.json());
  }

  async #rollbackParentLine(parentLineKey, sections) {
    try {
      await fetch(Theme.routes.cart_update_url, fetchConfig('json', {
        body: JSON.stringify({
          updates: {
            [parentLineKey]: 0,
          },
          sections,
        }),
      }));
    } catch (error) {
      console.error(error);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
      this.dataset.productUrl = event.detail.data.newProduct.url;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId, addToCartButtonContainer } = this.refs;

    const html = event.detail.data.html;
    const currentAddToCartButton = addToCartButtonContainer?.refs.addToCartButton;
    const newAddToCartButton = html?.querySelector('[ref="addToCartButton"]');
    const currentRingSizeSummary = this.querySelector('[data-ring-size-summary]');
    const nextRingSizeSummary = html?.querySelector('[data-ring-size-summary]');
    const currentWishlistSlot = /** @type {HTMLElement | null} */ (
      currentRingSizeSummary?.querySelector('.klaviyo-favorites-plp-slot') ?? null
    );
    const preservedWishlistContent = document.createDocumentFragment();
    const preserveWishlist = !event.detail.data.newProduct && Boolean(currentWishlistSlot?.querySelector('button'));
    const wishlistRenderedState = currentWishlistSlot?.dataset.rendered;
    const currentNotifyMeSummary = this.querySelector('[data-notify-me-summary]');
    const nextNotifyMeSummary = html?.querySelector('[data-notify-me-summary]');
    const currentPersonalisationRoot = this.querySelector('[data-personalisation-root]');
    const nextPersonalisationRoot = html?.querySelector('[data-personalisation-root]');

    if (!currentAddToCartButton) return;

    // Update the button state
    if (event.detail.resource == null || event.detail.resource.available == false) {
      addToCartButtonContainer.disable();
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
    } else {
      addToCartButtonContainer.enable();
      this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
    }

    // Update the add to cart button text and icon
    if (newAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    if (currentRingSizeSummary && nextRingSizeSummary) {
      if (preserveWishlist) {
        preservedWishlistContent.append(...currentWishlistSlot.childNodes);
      }

      morph(currentRingSizeSummary, nextRingSizeSummary);

      if (preserveWishlist) {
        const updatedWishlistSlot = currentRingSizeSummary.querySelector('.klaviyo-favorites-plp-slot');

        if (updatedWishlistSlot) {
          updatedWishlistSlot.replaceChildren(preservedWishlistContent);

          if (wishlistRenderedState) {
            updatedWishlistSlot.dataset.rendered = wishlistRenderedState;
          }
        }
      }
    }

    if (currentNotifyMeSummary && nextNotifyMeSummary) {
      morph(currentNotifyMeSummary, nextNotifyMeSummary);
    }

    if (currentPersonalisationRoot && nextPersonalisationRoot) {
      morph(currentPersonalisationRoot, nextPersonalisationRoot);
    }

    // Update the variant ID
    variantId.value = event.detail.resource.id ?? '';

    // Set the data attribute for the add to cart button to the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      productVariantMedia &&
        addToCartButtonContainer?.setAttribute('data-product-variant-media', productVariantMedia + '&width=100');
    }
  };

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable();
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}

class FlyToCart extends HTMLElement {
  /** @type {Element} */
  source;

  /** @type {Element} */
  destination;

  connectedCallback() {
    this.#animate();
  }

  #animate() {
    const rect = this.getBoundingClientRect();
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();

    //Define bezier curve points
    // Maybe add half of the size of the flying thingy to the x and y to make it center properly
    const offset = {
      x: rect.width / 2,
      y: rect.height / 2,
    };
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offset.x,
      y: sourceRect.top + sourceRect.height / 2 - offset.y,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - offset.x,
      y: destinationRect.top + destinationRect.height / 2 - offset.y,
    };

    //Calculate the control points
    const controlPoint1 = { x: startPoint.x, y: startPoint.y - 200 }; // Go up 200px
    const controlPoint2 = { x: endPoint.x - 300, y: endPoint.y - 100 }; // Go left 300px and up 100px

    //Animation variables
    /** @type {number | null} */
    let startTime = null;
    const duration = 600; // 600ms

    this.style.opacity = '1';

    /**
     * Animates the flying thingy along the bezier curve.
     * @param {number} currentTime - The current time.
     */
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Calculate current position along the bezier curve
      const position = bezierPoint(progress, startPoint, controlPoint1, controlPoint2, endPoint);

      //Update the position of the flying thingy
      this.style.setProperty('--x', `${position.x}px`);
      this.style.setProperty('--y', `${position.y}px`);

      // Scale down as it approaches the cart
      const scale = 1 - progress * 0.5;
      this.style.setProperty('--scale', `${scale}`);

      //Continue the animation if not finished
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        //Fade out the flying thingy
        this.style.opacity = '0';
        onAnimationEnd(this, () => this.remove());
      }
    };

    // Position the flying thingy back to the start point
    this.style.setProperty('--x', `${startPoint.x}px`);
    this.style.setProperty('--y', `${startPoint.y}px`);

    //Start the animation
    requestAnimationFrame(animate);
  }
}

/**
 * Calculates a point on a cubic Bézier curve.
 * @param {number} t - The parameter value (0 <= t <= 1).
 * @param {{x: number, y: number}} p0 - The starting point (x, y).
 * @param {{x: number, y: number}} p1 - The first control point (x, y).
 * @param {{x: number, y: number}} p2 - The second control point (x, y).
 * @param {{x: number, y: number}} p3 - The ending point (x, y).
 * @returns {{x: number, y: number}} The point on the curve.
 */
function bezierPoint(t, p0, p1, p2, p3) {
  const cX = 3 * (p1.x - p0.x);
  const bX = 3 * (p2.x - p1.x) - cX;
  const aX = p3.x - p0.x - cX - bX;

  const cY = 3 * (p1.y - p0.y);
  const bY = 3 * (p2.y - p1.y) - cY;
  const aY = p3.y - p0.y - cY - bY;

  const x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x;
  const y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y;

  return { x, y };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}
